import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/apiClient";
import type { BlogPostStatus, BlogPostSummary } from "../api/types";
import { NewBlogPostModal } from "../components/NewBlogPostModal";

const STATUS_CLASS: Record<BlogPostStatus, string> = {
  NEW: "badge muted",
  GENERATING: "badge primary",
  DRAFT_READY: "badge primary",
  EDITING: "badge primary",
  PUBLISHING: "badge primary",
  POSTED: "badge success",
  FAILED: "badge danger",
};

const STATUS_LABEL: Record<BlogPostStatus, string> = {
  NEW: "Queued",
  GENERATING: "Generating",
  DRAFT_READY: "Draft ready",
  EDITING: "AI editing",
  PUBLISHING: "Publishing",
  POSTED: "Posted",
  FAILED: "Failed",
};

const STATUS_FILTERS: Array<{ key: "ALL" | BlogPostStatus; label: string }> = [
  { key: "ALL", label: "All" },
  { key: "DRAFT_READY", label: "Draft ready" },
  { key: "POSTED", label: "Posted" },
  { key: "GENERATING", label: "In progress" },
  { key: "FAILED", label: "Failed" },
];

function shortDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + "…";
}

export function BlogPostsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BlogPostSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<"ALL" | BlogPostStatus>("ALL");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setRows(await api.listBlogPosts());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "ALL" && r.status !== filter) return false;
      if (q && !r.idea.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filter, search]);

  const handleCreated = (created: BlogPostSummary) => {
    setRows((prev) => [created, ...prev]);
    setModalOpen(false);
    navigate(`/app/blog-posts/${created.id}`);
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <h2 className="page-title">Blog Posts</h2>
          <p className="page-subtitle">
            Articles researched and drafted by the writing agent, reviewed here, published to Shopify.
          </p>
        </div>
        <button type="button" className="button" onClick={() => setModalOpen(true)}>
          Write a new Blog Post
        </button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div
        className="card"
        style={{
          padding: "var(--space-3)",
          display: "flex",
          gap: "var(--space-3)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? "button" : "button tinted"}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Search by idea…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: "1 1 240px", minWidth: 200 }}
        />
      </div>

      <div className="card table-scroll">
        <table>
          <thead>
            <tr>
              <th>Idea</th>
              <th>Language</th>
              <th>Status</th>
              <th>Last event</th>
              <th>Created</th>
              <th>Posted</th>
              <th>Shopify</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                  No blog posts yet. Click <strong>Write a new Blog Post</strong> to begin.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/app/blog-posts/${r.id}`)}
                >
                  <td style={{ maxWidth: 360 }}>{truncate(r.idea, 80)}</td>
                  <td>
                    <span style={{ textTransform: "uppercase" }}>{r.language}</span>
                  </td>
                  <td>
                    <span className={STATUS_CLASS[r.status]}>{STATUS_LABEL[r.status]}</span>
                  </td>
                  <td style={{ maxWidth: 240, color: "var(--color-text-muted)" }}>
                    {r.lastEvent ?? "—"}
                  </td>
                  <td>{shortDate(r.createdAt)}</td>
                  <td>{shortDate(r.postedAt)}</td>
                  <td>
                    {r.shopifyArticleUrl ? (
                      <a
                        href={r.shopifyArticleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View ↗
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen ? (
        <NewBlogPostModal
          onClose={() => setModalOpen(false)}
          onCreated={handleCreated}
        />
      ) : null}
    </section>
  );
}
